import { useEffect, useRef } from 'react';

const BAR_W = 3;
const GAP = 2;
const MIN_H = 2;

/**
 * The bars. Canvas rather than a few dozen DOM nodes, because this also has
 * to animate at frame rate while recording.
 */
export default function Waveform({
  peaks = [], progress = 0, live = false, height = 34, onSeek,
}) {
  const canvasRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const box = boxRef.current;
    if (!canvas || !box) return;

    const width = box.clientWidth || 200;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const styles = getComputedStyle(canvas);
    const played = styles.getPropertyValue('--wave-played').trim() || '#6366f1';
    const rest = styles.getPropertyValue('--wave-rest').trim() || 'rgba(148,163,184,0.38)';

    const slots = Math.max(1, Math.floor((width + GAP) / (BAR_W + GAP)));
    // While recording the newest sample belongs at the right-hand edge, so the
    // drawing scrolls the way a level meter does.
    const source = live ? peaks.slice(-slots) : peaks;
    if (source.length === 0) return;

    for (let i = 0; i < slots; i += 1) {
      const at = source.length === slots
        ? source[i]
        : source[Math.floor((i / slots) * source.length)] ?? 0;
      const barHeight = Math.max(MIN_H, at * (height - 2));
      const x = i * (BAR_W + GAP);
      const y = (height - barHeight) / 2;
      ctx.fillStyle = live || i / slots <= progress ? played : rest;
      ctx.beginPath();
      ctx.roundRect(x, y, BAR_W, barHeight, BAR_W / 2);
      ctx.fill();
    }
  }, [peaks, progress, live, height]);

  const seek = (event) => {
    if (!onSeek) return;
    const box = boxRef.current.getBoundingClientRect();
    onSeek(Math.min(1, Math.max(0, (event.clientX - box.left) / box.width)));
  };

  return (
    <div
      ref={boxRef}
      className={`waveform ${onSeek ? 'seekable' : ''}`}
      style={{ height }}
      onClick={seek}
      role={onSeek ? 'slider' : undefined}
      aria-label={onSeek ? 'Seek' : undefined}
      aria-valuenow={onSeek ? Math.round(progress * 100) : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      tabIndex={onSeek ? 0 : undefined}
      onKeyDown={onSeek ? (event) => {
        if (event.key === 'ArrowRight') onSeek(Math.min(1, progress + 0.05));
        if (event.key === 'ArrowLeft') onSeek(Math.max(0, progress - 0.05));
      } : undefined}
    >
      <canvas ref={canvasRef} />
    </div>
  );
}
